import { 
    useState, 
    useEffect, 
    useRef, 
    forwardRef, 
    useImperativeHandle 
} from 'react';
import Tooltip from '@mui/material/Tooltip';
import Spinner from './Spinner';

const ImageCaptureForLawyer = forwardRef(({ onImageChange, preLoadedImage, keyName }, ref) => {

    const [cameras, setCameras] = useState([]);
    const [selectedCamera, setSelectedCamera] = useState(null);
    const [isModalOpen, setModalOpen] = useState(false);

    const [image, setImage] = useState(null);
    const [imageName, setImageName] = useState('');
    const [preview, setPreview] = useState(preLoadedImage);
    const [isCameraOpen, setIsCameraOpen] = useState(false);

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);
    const streamRef = useRef(null);

    const [isAnimating, setIsAnimating] = useState(false);

    const playClickSound = () => {
        const clickSound = new Audio('/iusevolutione/assets/audio/camera_shutter.mp3');
        clickSound.play();
    };

    /* =========================
       GET AVAILABLE CAMERAS
    ========================== */
    useEffect(() => {
        navigator.mediaDevices.enumerateDevices()
            .then(devices => {
                const videoDevices = devices.filter(d => d.kind === 'videoinput');
                setCameras(videoDevices);
                if (videoDevices.length && !selectedCamera) {
                    setSelectedCamera(videoDevices[0].deviceId);
                }
            })
            .catch(err => console.error(err));
    }, []);

    /* =========================
       PRELOADED IMAGE
    ========================== */
    useEffect(() => {
        if (!preLoadedImage) return;

        if (typeof preLoadedImage === 'string') {
            setPreview(preLoadedImage);
        } else if (preLoadedImage instanceof File) {
            const reader = new FileReader();
            reader.onloadend = () => setPreview(reader.result);
            reader.readAsDataURL(preLoadedImage);
        }

        console.log("preLoadedImage",preLoadedImage);
    }, [preLoadedImage]);

    /* =========================
       OPEN CAMERA (ONCE)
    ========================== */
    const openCamera = async () => {
        if (streamRef.current) return;

        setIsCameraOpen(true);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    deviceId: selectedCamera ? { exact: selectedCamera } : undefined,
                    width: 275,
                    height: 275
                }
            });

            streamRef.current = stream;
            videoRef.current.srcObject = stream;
            await videoRef.current.play();
        } catch (err) {
            console.error('Camera error:', err);
        }
    };

    /* =========================
       SNAPSHOT (LIVE VIDEO KEEPS GOING)
    ========================== */
    const takePicture = () => {
        if (!videoRef.current || !canvasRef.current) return;

        //playClickSound();
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 300);

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        canvas.width = 1080;
        canvas.height = 1080;

        ctx.drawImage(videoRef.current, 0, 0, 1080, 1080);

        const dataUrl = canvas.toDataURL('image/png');
        setPreview(dataUrl);

        fetch(dataUrl)
            .then(res => res.blob())
            .then(blob => {
                const file = new File([blob], `${keyName}.png`, { type: 'image/png' });
                setImage(file);
                setImageName(file.name);
                onImageChange(file, keyName);
            });
    };

    /* =========================
       FILE UPLOAD
    ========================== */
    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setImage(file);
        setImageName(file.name);

        const reader = new FileReader();
        reader.onload = () => setPreview(reader.result);
        reader.readAsDataURL(file);

        onImageChange(file, keyName);
    };

    const handleButtonClick = () => {
        fileInputRef.current.click();
    };

    const handleCameraChange = (e) => {
        setSelectedCamera(e.target.value);
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        openCamera();
    };

    /* =========================
       CLEANUP
    ========================== */
    useEffect(() => {
        return () => {
            streamRef.current?.getTracks().forEach(t => t.stop());
        };
    }, []);


    useEffect(() => {
        openCamera();
    }, []);

    useImperativeHandle(ref, () => ({
        takePicture
    }));

    /* =========================
       RENDER
    ========================== */
    return ( 
      <>



 {preview ? (
        <div className={`captured-image me-2 mt-2  ${isAnimating ? 'camera-blinking' : ''}`} 
        style={{ border: "1px solid white", borderRadius: 10, objectFit: "cover" }}>
            <img
                src={preview}
                alt="Preview"
                className="rounded w-100 h-100"
                style={{ objectFit: 'contain',
                  transform: 'scaleX(-1)',
                 }}
            />
        </div>
    )
    :

    <div className="captured-image me-2 mt-2  bg-white d-flex justify-content-center text-danger" style={{ border: "1px solid white", borderRadius: 10 }}>
      <Spinner text="" />
    </div>
  
  }





                <input
                    type="file"
                    className="d-none"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                />

                <div className="d-flex justify-content-center">
                    <div className="d-flex flex-column align-items-center">

                        {isCameraOpen && (
                            <div className="position-relative rounded">
                                <video
                                    ref={videoRef}
                                    className="rounded"
                                    style={{ width: 250, height: 250,display:'none' }}
                                />
                                <canvas ref={canvasRef} style={{ display: 'none' }} />
                            </div>
                        )}

                       

                    </div>
                </div> 
                <div className="d-flex flex-column text-center fw-bold">

                    {isCameraOpen && cameras.length > 0 && (
                        <select
                            className="form-control mb-2 d-none"
                            value={selectedCamera}
                            onChange={handleCameraChange}
                        >
                            {cameras.map(cam => (
                                <option key={cam.deviceId} value={cam.deviceId}>
                                    {cam.label || 'Camera'}
                                </option>
                            ))}
                        </select>
                    )}

                    <div className="btn-group w-100">
                        {!isCameraOpen && (
                            <Tooltip title="Open Camera">
                                <button className="btn btn-outline-secondary" onClick={openCamera}>
                                    <i className="bx bxs-camera" />
                                </button>
                            </Tooltip>
                        )}

                        {/* <Tooltip title="Take Shot">
                            <button className="btn btn-outline-secondary" onClick={takePicture}>
                                <i className="bx bx-aperture" />
                            </button>
                        </Tooltip> */}

                    </div>
                </div>
      </>
    );
});

export default ImageCaptureForLawyer;
